import ipaddr from "ipaddr.js";

/**
 * Normalizes an IP address or CIDR range typed by an admin: "203.0.113.7",
 * "203.0.113.0/24", "2001:db8::/32". A range is stored as its network address; a single
 * address without a suffix. Returns null when it is neither. Shorthand IPv4 ("127.1") is
 * refused: it is never what someone meant. An IPv4-mapped IPv6 entry ("::ffff:203.0.113.7",
 * "::ffff:203.0.113.0/120") is stored as the IPv4 it means, since callers are matched as IPv4;
 * a mapped range wider than /96 is refused (it would reach beyond the mapped space).
 */
export function normalizeCidr(input: string): string | null {
	const [address = "", bits, ...rest] = input.trim().split("/");
	if (rest.length > 0) {
		return null;
	}
	const v4 = ipaddr.IPv4.isValidFourPartDecimal(address);
	if (!v4 && !(ipaddr.IPv6.isValid(address) && !address.includes("%"))) {
		return null;
	}
	const max = v4 ? 32 : 128;
	const prefix =
		bits === undefined ? max : /^\d{1,3}$/.test(bits) ? Number(bits) : -1;
	if (prefix < 0 || prefix > max) {
		return null;
	}
	if (!v4) {
		const parsed = ipaddr.IPv6.parse(address);
		if (parsed.isIPv4MappedAddress()) {
			return prefix < 96
				? null
				: normalizeCidr(
						`${parsed.toIPv4Address().toString()}/${prefix - 96}`,
					);
		}
	}
	const network = v4
		? ipaddr.IPv4.networkAddressFromCIDR(`${address}/${prefix}`).toString()
		: ipaddr.IPv6.networkAddressFromCIDR(
				`${address}/${prefix}`,
			).toRFC5952String();
	return prefix === max ? network : `${network}/${prefix}`;
}

/** Whether `ip` is inside one of `cidrs`. An empty list allows every address. */
export function ipAllowed(
	ip: string | null,
	cidrs: readonly string[],
): boolean {
	if (cidrs.length === 0) {
		return true;
	}
	if (!ip || !ipaddr.isValid(ip)) {
		return false;
	}
	const address = ipaddr.process(ip);
	return cidrs.some((cidr) => {
		const [range, bits] = cidr.includes("/")
			? ipaddr.parseCIDR(cidr)
			: [ipaddr.parse(cidr), address.kind() === "ipv4" ? 32 : 128];
		return range.kind() === address.kind() && address.match(range, bits);
	});
}
